'use client';

import React, { useState, useMemo } from 'react';
import {
    Briefcase,
    Search,
    Filter,
    Plus,
    Users,
    Calendar,
    Mail,
    Phone,
    MapPin,
    ArrowLeft,
    Wallet,
    History,
    FileText,
    CheckCircle2,
    ShieldCheck,
    ChevronRight,
    Download,
    Eye,
    GraduationCap,
    BadgeCheck,
    MoreHorizontal,
    Settings2,
    Settings
} from 'lucide-react';
import Link from 'next/link';

export default function StaffPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPosition, setFilterPosition] = useState('Semua Jabatan');

    const staffMembers = [
        {
            id: 'SDM-002',
            nipn: '199203102022011004',
            name: 'Budi Santoso',
            role: 'Operasional',
            jabatan: 'Bendahara Unit',
            unit: 'PAUD Unit 1',
            type: 'Staff Tetap',
            email: 'budi.s@sekolah.sch.id',
            phone: '0813-1122-3344',
            joinDate: '01 Jan 2022',
            salaryBasis: 'Rp 4.000.000',
            address: 'Gg. Kancil, Pekanbaru',
            status: 'active'
        },
        {
            id: 'SDM-004',
            nipn: '-',
            name: 'Andi Wijaya',
            role: 'Keamanan',
            jabatan: 'Security',
            unit: 'PAUD Unit 1',
            type: 'Staff Honorer',
            email: 'andi.w@sekolah.sch.id',
            phone: '0812-3344-5566',
            joinDate: '10 Feb 2024',
            salaryBasis: 'Rp 2.500.000',
            address: 'Jl. Garuda, Pekanbaru',
            status: 'active'
        },
        {
            id: 'SDM-006',
            nipn: '199305152023012006',
            name: 'Dewi Lestari',
            role: 'Administrasi',
            jabatan: 'Sekretaris Unit',
            unit: 'PAUD Unit 2',
            type: 'Staff Tetap',
            email: 'dewi.l@sekolah.sch.id',
            phone: '0852-1122-9988',
            joinDate: '15 Jul 2023',
            salaryBasis: 'Rp 3.800.000',
            address: 'Jl. Parit Indah, Pekanbaru',
            status: 'active'
        },
        {
            id: 'SDM-008',
            nipn: '-',
            name: 'Sutrisno',
            role: 'Kebersihan',
            jabatan: 'Office Boy',
            unit: 'PAUD Unit 1',
            type: 'Staff Honorer',
            email: 'sutrisno@sekolah.sch.id',
            phone: '0812-9988-7766',
            joinDate: '01 Mar 2024',
            salaryBasis: 'Rp 2.200.000',
            address: 'Jl. Melati, Pekanbaru',
            status: 'active'
        },
        {
            id: 'SDM-010',
            nipn: '198910052021011007',
            name: 'Hendra Wijaya',
            role: 'IT Support',
            jabatan: 'Admin IT',
            unit: 'PAUD Unit 2',
            type: 'Staff Tetap',
            email: 'hendra.w@sekolah.sch.id',
            phone: '0813-5566-7788',
            joinDate: '01 Jan 2021',
            salaryBasis: 'Rp 4.500.000',
            address: 'Jl. Arifin Ahmad, Pekanbaru',
            status: 'active'
        }
    ];

    const positions = useMemo(() => {
        const unique = Array.from(new Set(staffMembers.map(g => g.role)));
        return ['Semua Jabatan', ...unique];
    }, []);

    const filteredStaff = useMemo(() => {
        return staffMembers.filter(g => {
            const matchesSearch = g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                g.nipn.includes(searchTerm) ||
                g.id.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesPosition = filterPosition === 'Semua Jabatan' || g.role === filterPosition;
            return matchesSearch && matchesPosition;
        });
    }, [searchTerm, filterPosition]);

    const stats = {
        total: staffMembers.length,
        tetap: staffMembers.filter(g => g.type.includes('Tetap')).length,
        honorer: staffMembers.filter(g => g.type.includes('Honorer')).length,
        aktif: staffMembers.filter(g => g.status === 'active').length,
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            {/* Header section */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="relative z-10 space-y-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <Briefcase className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Data <span className="text-primary">Staff</span></h1>
                                <p className="text-secondary font-medium flex items-center gap-2">
                                    Manajemen Tenaga Operasional & Administrasi Sekolah
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button className="px-6 py-3.5 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-white hover:border-primary transition-all shadow-sm flex items-center gap-2 uppercase tracking-widest">
                                <Download className="w-4 h-4 text-muted" /> Export
                            </button>
                            <button className="px-6 py-3.5 gradient-green text-white rounded-2xl text-[10px] font-bold shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex items-center gap-2 uppercase tracking-widest">
                                <Plus className="w-4 h-4" /> Tambah Staff
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Grid */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { label: 'Total Staff', value: stats.total, icon: Briefcase, color: 'text-primary', bg: 'bg-primary-soft' },
                    { label: 'Staff Tetap', value: stats.tetap, icon: ShieldCheck, color: 'text-success', bg: 'bg-success-soft' },
                    { label: 'Staff Honorer', value: stats.honorer, icon: Settings, color: 'text-warning', bg: 'bg-warning-soft' },
                    { label: 'Status Aktif', value: stats.aktif, icon: BadgeCheck, color: 'text-info', bg: 'bg-info-soft' },
                ].map((item, idx) => (
                    <div key={idx} className="bg-surface p-6 rounded-4xl border border-border-main shadow-sm group hover:shadow-md transition-all">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 ${item.bg} ${item.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                <item.icon className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">{item.label}</p>
                                <p className="text-2xl font-bold text-foreground">{item.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* Table Section */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden min-h-[500px] flex flex-col">
                {/* Search & Filter Bar */}
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari nama, NIPN..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-background border border-border-main px-4 py-2 rounded-2xl">
                            <Filter className="w-4 h-4 text-muted" />
                            <select
                                className="bg-transparent border-none text-[10px] font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterPosition}
                                onChange={(e) => setFilterPosition(e.target.value)}
                            >
                                {positions.map(pos => (
                                    <option key={pos} value={pos}>{pos}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table Content */}
                <div className="overflow-x-auto grow">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">NIPN & IDENTITAS</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Jabatan</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Status</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {filteredStaff.length > 0 ? filteredStaff.map((staff) => (
                                <tr key={staff.id} className="hover:bg-primary-soft/10 transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary/10 to-primary/20 flex items-center justify-center text-primary font-bold text-xs uppercase shadow-inner">
                                                {staff.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{staff.name}</p>
                                                <p className="text-[10px] text-muted font-bold tabular-nums">NIPN: {staff.nipn}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-foreground">{staff.jabatan}</p>
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-border-main"></div>
                                                <p className="text-[10px] text-secondary font-medium uppercase tracking-wider">{staff.role}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border ${staff.type.includes('Tetap')
                                            ? 'bg-success-soft text-success border-success/10'
                                            : 'bg-warning-soft text-warning border-warning/10'
                                            }`}>
                                            {staff.type}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap">
                                        <button className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-primary hover:border-primary transition-all shadow-sm">
                                            <Eye className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center">
                                        <p className="text-sm text-secondary font-medium">Data staff tidak ditemukan</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-border-main bg-background/10 flex justify-between items-center">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">
                        Menampilkan {filteredStaff.length} dari {stats.total} Staff
                    </p>
                    <div className="flex gap-2">
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Prev</button>
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Next</button>
                    </div>
                </div>
            </section>
        </main>
    );
}
